import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import ReadMail from './readMail';
import './shimmer.css'

const getTagValues = (value) => (Array.isArray(value) ? value : []);

const renderTagGroup = (title, values = []) => {
    const tagValues = getTagValues(values);

    return (
        <div className="mail-tag-tooltip-section">
            <div className="mail-tag-tooltip-heading">{title}</div>
            {tagValues.length ? (
                <div className="mail-tag-tooltip-values">
                    {tagValues.map((tagName) => (
                        <span key={`${title}-${tagName}`} className="mail-tag-tooltip-chip">
                            {tagName}
                        </span>
                    ))}
                </div>
            ) : (
                <div className="mail-tag-tooltip-empty">None</div>
            )}
        </div>
    );
};

const truncateText = (value = '', maxLength = 180) => {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (!normalized) {
        return '';
    }

    if (normalized.length <= maxLength) {
        return normalized;
    }

    return `${normalized.slice(0, maxLength)}...`;
};

const getAppSignatureName = (mail = {}) => String(mail?.appSignatureName || '').trim();

const MailSenderDisplay = ({ mail, isUnread, onOpenMail }) => {
    const senderLabel = mail.fromName ? mail.fromName : mail.fromAddress;
    const appSignatureName = getAppSignatureName(mail);
    const senderButton = (
        <button
            type="button"
            className={`title ${isUnread ? 'fw-bold' : ''} mail-link-btn`}
            onClick={onOpenMail}
        >
            {senderLabel}
        </button>
    );

    if (!appSignatureName) {
        return senderButton;
    }

    return (
        <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="top"
            overlay={(
                <Tooltip
                    id={`mail-signature-tooltip-${mail.messageId}`}
                    className="mail-signature-tooltip"
                >
                    {appSignatureName}
                </Tooltip>
            )}
        >
            <span className="mail-signature-tooltip-trigger">{senderButton}</span>
        </OverlayTrigger>
    );
};

const MailTagIndicator = ({ mail }) => {
    const tagSummary = mail?.tagSummary || {};
    const userTags = getTagValues(tagSummary.userTags);
    const categoryTags = getTagValues(tagSummary.categoryTags);
    const privateTags = getTagValues(tagSummary.privateTags);
    const otherTags = getTagValues(tagSummary.otherTags);

    const computedTagCount =
        userTags.length +
        categoryTags.length +
        privateTags.length +
        otherTags.length;

    const totalTagCount = Number(tagSummary.totalCount);
    const tagCount = Number.isFinite(totalTagCount) && totalTagCount > 0
        ? totalTagCount
        : computedTagCount;

    const hasTags = Boolean(tagSummary.hasTags || tagCount > 0);
    if (!hasTags) {
        return null;
    }

    return (
        <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="top"
            overlay={(
                <Tooltip
                    id={`mail-tag-tooltip-${mail.messageId}`}
                    className="mail-tag-tooltip"
                >
                    {renderTagGroup('Users', userTags)}
                    {renderTagGroup('Categories', categoryTags)}
                    {renderTagGroup('Private', privateTags)}
                </Tooltip>
            )}
        >
            <span
                className="mail-tag-indicator"
                role="button"
                tabIndex={0}
                aria-label={`Tagged email with ${tagCount} tags`}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                }}
            >
                <i className="mdi mdi-tag-multiple text-muted" />
                <span className="mail-tag-count">{tagCount}</span>
            </span>
        </OverlayTrigger>
    );
};

const MailCommentIndicator = ({ mail }) => {
    const commentSummary = mail?.commentSummary || {};
    const commentItems = getTagValues(commentSummary.items);

    const computedCount = commentItems.length;
    const totalCount = Number(commentSummary.totalCount);
    const commentCount = Number.isFinite(totalCount) && totalCount > 0
        ? totalCount
        : computedCount;
    const hasComments = Boolean(commentSummary.hasComments || commentCount > 0);

    if (!hasComments) {
        return null;
    }

    const remainingCountRaw = Number(commentSummary.remainingCount);
    const remainingCount = Number.isFinite(remainingCountRaw) && remainingCountRaw > 0
        ? remainingCountRaw
        : Math.max(0, commentCount - commentItems.length);

    return (
        <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="top"
            overlay={(
                <Tooltip
                    id={`mail-comment-tooltip-${mail.messageId}`}
                    className="mail-comment-tooltip"
                >
                    <div className="mail-comment-tooltip-section">
                        <div className="mail-comment-tooltip-heading">Comments</div>
                        {commentItems.length ? (
                            <div className="mail-comment-tooltip-list">
                                {commentItems.map((item) => {
                                    const userName = String(item?.userName || '').trim() || 'Unknown user';
                                    const createdOn = item?.createdOn ? moment(item.createdOn).format('DD MMM YYYY, hh:mm A') : '';
                                    const commentText = truncateText(item?.comment, 200);

                                    return (
                                        <div
                                            key={`comment-${mail.messageId}-${item?.id || `${userName}-${createdOn}-${commentText}`}`}
                                            className="mail-comment-tooltip-item"
                                        >
                                            <div className="mail-comment-tooltip-meta">
                                                {userName}
                                                {createdOn ? ` • ${createdOn}` : ''}
                                            </div>
                                            <div className="mail-comment-tooltip-text">{commentText || '-'}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="mail-comment-tooltip-empty">None</div>
                        )}
                        {remainingCount > 0 && (
                            <div className="mail-comment-tooltip-more">
                                +{remainingCount} more comment{remainingCount > 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                </Tooltip>
            )}
        >
            <span
                className="mail-comment-indicator"
                role="button"
                tabIndex={0}
                aria-label={`Email has ${commentCount} comments`}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                }}
            >
                <i className="mdi mdi-comment-text-multiple-outline text-muted" />
                <span className="mail-comment-count">{commentCount}</span>
            </span>
        </OverlayTrigger>
    );
};

const MailResponseIndicator = ({ mail }) => {
    const isReplied = Boolean(mail?.isReplied);
    const isForwarded = Boolean(mail?.isForwarded);

    if (!isReplied && !isForwarded) {
        return null;
    }

    return (
        <span className="mail-response-indicator" aria-label="Mail action status">
            {isReplied && (
                <i
                    className="mdi mdi-reply mail-response-icon-replied"
                    title="Replied"
                    aria-label="Replied"
                />
            )}
            {isForwarded && (
                <i
                    className="mdi mdi-forward mail-response-icon-forwarded"
                    title="Forwarded"
                    aria-label="Forwarded"
                />
            )}
        </span>
    );
};

const RightSidebar = ({
    data,
    setPage,
    showLoader,
    readmail,
    setReadmail,
    folderName,
    folderDisplayName,
    handleMailAction,
    selectedMessageIds,
    setSelectedMessageIds,
    onRefresh,
    host,
    selectedMailboxId,
    selectedMailboxEmail,
    searchInput,
    setSearchInput,
    searchQuery,
    onSearchSubmit,
    onSearchClear
}) => {

    const { page = 1, limit = 10, mails = [], totalMessages = 0 } = data || {};
    const [selectAll, setSelectAll] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchContainerRef = useRef(null);

    const totalPages = Math.ceil(totalMessages / limit);
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, totalMessages);

    const handlePrev = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNext = () => {
        if (page < totalPages) setPage(page + 1);
    };

    const handleCheckboxChange = (messageId) => {
        setSelectedMessageIds(prev =>
            prev.includes(messageId)
                ? prev.filter(id => id !== messageId) // Uncheck
                : [...prev, messageId] // Check
        );
    };

    useEffect(() => {
        if (!mails.length) {
            setSelectAll(false);
            return;
        }
        const allSelected = mails.every((mail) => selectedMessageIds.includes(mail.messageId));
        setSelectAll(allSelected);
    }, [mails, selectedMessageIds]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const normalizedInput = searchInput.trim().toLowerCase();
    const sortedMails = useMemo(() => {
        const parseDate = (value) => {
            const time = new Date(value).getTime();
            return Number.isNaN(time) ? 0 : time;
        };

        return [...mails].sort((a, b) => parseDate(b.date) - parseDate(a.date));
    }, [mails]);

    const suggestions = useMemo(() => {
        if (!normalizedInput) {
            return [];
        }

        const uniqueValues = new Set();
        const matched = [];

        for (const mail of mails) {
            const candidates = [mail.fromName, mail.fromAddress, mail.subject];

            for (const value of candidates) {
                const cleanValue = (value || '').trim();
                if (!cleanValue) {
                    continue;
                }

                const searchValue = cleanValue.toLowerCase();
                if (!searchValue.includes(normalizedInput) || uniqueValues.has(searchValue)) {
                    continue;
                }

                uniqueValues.add(searchValue);
                matched.push(cleanValue);

                if (matched.length >= 8) {
                    return matched;
                }
            }
        }

        return matched;
    }, [mails, normalizedInput]);

    const handleSearchAction = (value = searchInput) => {
        onSearchSubmit(value);
        setShowSuggestions(false);
    };

    const handleSearchInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearchAction();
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchInput(suggestion);
        handleSearchAction(suggestion);
    };

    const hasSelectedMessages = selectedMessageIds.length > 0;
    const fallbackFolderName = folderName?.split('/')[folderName?.split('/')?.length - 1];
    const resolvedFolderDisplayName = folderDisplayName || fallbackFolderName || 'Inbox';
    const openMail = (mail) => {
        setReadmail(mail.read === false ? { ...mail, read: true } : mail);
        if (mail.read === false) {
            handleMailAction(mail.messageId, 'read');
        }
    };

    return (
        <div className="email-rightbar mb-3">
            <div className="card">

                {
                    showLoader &&
                    (
                        <div className="shimmer-mailbox">
                            {[...Array(limit)].map((_, idx) => (
                                <div key={idx} className="shimmer-mail-row">
                                    <div className="shimmer-checkbox" />
                                    <div className="shimmer-from" />
                                    <div className="shimmer-subject" />
                                    <div className="shimmer-date" />
                                </div>
                            ))}
                        </div>
                    )}

                {!readmail && !showLoader &&
                    <div className="px-3 pt-3 pb-0">
                        <h5 className="mb-0">{resolvedFolderDisplayName}</h5>
                    </div>
                }

                {!readmail && !showLoader &&

                    <div className="btn-toolbar p-3 gap-2 d-flex justify-content-between" role="toolbar">
                        <div ref={searchContainerRef} className="position-relative" style={{ maxWidth: '500px', width: '100%' }}>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="mdi mdi-magnify" />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by sender, subject, or content"
                                    value={searchInput}
                                    onChange={(e) => {
                                        setSearchInput(e.target.value);
                                        setShowSuggestions(true);
                                    }}
                                    onFocus={() => setShowSuggestions(Boolean(searchInput.trim()))}
                                    onKeyDown={handleSearchInputKeyDown}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => handleSearchAction()}
                                >
                                    Search
                                </button>
                                {searchInput && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            onSearchClear();
                                            setShowSuggestions(false);
                                        }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {showSuggestions && suggestions.length > 0 && (
                                <div
                                    className="list-group position-absolute w-100 shadow-sm"
                                    style={{ zIndex: 1050, top: '100%', maxHeight: '220px', overflowY: 'auto' }}
                                >
                                    {suggestions.map((suggestion) => (
                                        <button
                                            key={suggestion}
                                            type="button"
                                            className="list-group-item list-group-item-action text-start"
                                            onClick={() => handleSuggestionClick(suggestion)}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="btn-group me-2 mb-2 mb-sm-0"

                        >
                            <button type="button" className="btn btn-primary waves-light waves-effect"
                                style={{
                                    backgroundColor: '#6895b5',
                                    color: 'white'
                                }}
                            >
                                <input
                                    style={{
                                        width: '15px',
                                        height: '15px',
                                        marginTop: '4px',
                                    }}
                                    type="checkbox"
                                    id="chk_all"
                                    checked={selectAll}
                                    onChange={() => {
                                        const allIds = selectAll ? [] : mails.map(mail => mail.messageId);
                                        setSelectedMessageIds(allIds);
                                        setSelectAll(!selectAll);
                                    }}
                                />
                                <label htmlFor="chk_all" className="toggle" />
                            </button>
                            <button type="button" className="btn btn-primary waves-light waves-effect"
                                style={{
                                    backgroundColor: '#6895b5',
                                    color: 'white',
                                    fontSize: '20px'
                                }}
                                onClick={() => { onRefresh() }}
                            >
                                <i className="mdi mdi-refresh" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary waves-light waves-effect"
                                style={{
                                    backgroundColor: '#6895b5',
                                    color: 'white'
                                }}
                                title="Mark selected as read"
                                disabled={!hasSelectedMessages}
                                onClick={() => {
                                    handleMailAction(selectedMessageIds, 'read')
                                }}
                            >
                                <i className="mdi mdi-email-open-outline" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary waves-light waves-effect"
                                style={{
                                    backgroundColor: '#6895b5',
                                    color: 'white'
                                }}
                                title="Mark selected as unread"
                                disabled={!hasSelectedMessages}
                                onClick={() => {
                                    handleMailAction(selectedMessageIds, 'unread')
                                }}
                            >
                                <i className="mdi mdi-email-outline" />
                            </button>
                        </div>

                    </div>

                }

                {readmail && !showLoader && <ReadMail
                    data={readmail}
                    closeMail={() => {
                        setReadmail(false);
                        if (typeof onRefresh === 'function') {
                            onRefresh();
                        }
                    }}
                    handleMailAction={handleMailAction}
                    selectedMailboxId={selectedMailboxId}
                    selectedMailboxEmail={selectedMailboxEmail}
                    folder={folderName}
                    isDraftFolder={String(resolvedFolderDisplayName).toLowerCase() === 'drafts'}
                />}
                {!readmail && !showLoader && <ul className="message-list">
                    {sortedMails.map((mail) => {
                        const isUnread = mail.read === false;
                        const hasAttachments = mail.hasAttachments || (mail.attachments?.length || 0) > 0;
                        const formattedMailDate = mail?.date && moment(mail.date).isValid()
                            ? moment(mail.date).format('MMM DD YYYY, hh:mm A')
                            : '';

                        const isChecked = selectedMessageIds.includes(mail.messageId);

                        return (
                            <li
                                key={mail.messageId}
                                className={`mail-item ${isUnread ? 'mail-item-unread' : 'mail-item-read'}`}
                            >
                                <div className="col-mail col-mail-1">
                                    <div className="checkbox-wrapper-mail">
                                        <input
                                            type="checkbox"
                                            id={`chk_${mail.messageId}`}
                                            checked={isChecked}
                                            onChange={() => handleCheckboxChange(mail.messageId)}
                                        />
                                        <label htmlFor={`chk_${mail.messageId}`} className="toggle" />
                                    </div>
                                    <span className={`mail-unread-dot ${isUnread ? 'visible' : ''}`} />
                                    <MailSenderDisplay
                                        mail={mail}
                                        isUnread={isUnread}
                                        onOpenMail={() => {
                                            openMail(mail);
                                        }}
                                    />
                                </div>
                                <div className="col-mail col-mail-2" onClick={() => {
                                    openMail(mail);
                                }}>
                                    <button
                                        type="button"
                                        className={`subject ${isUnread ? 'fw-bold' : ''} mail-link-btn mail-subject-btn`}
                                    >
                                        {hasAttachments && (
                                            <i className="mdi mdi-paperclip text-muted me-1" title="Has attachment" />
                                        )}
                                        <MailResponseIndicator mail={mail} />
                                        <MailTagIndicator mail={mail} />
                                        <MailCommentIndicator mail={mail} />
                                        <span className="text-truncate" style={{ minWidth: 0 }}>
                                            {mail.subject}
                                            {mail.text ? (
                                                <span className="text-muted"> - {mail.text}</span>
                                            ) : null}
                                        </span>
                                    </button>
                                    <div className={`date mail-date-wrap ${isUnread ? 'fw-bold' : ''}`}>
                                        <span>{formattedMailDate}</span>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-muted mail-toggle-read-btn"
                                            title={isUnread ? 'Mark as read' : 'Mark as unread'}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                handleMailAction(mail.messageId, isUnread ? 'read' : 'unread');
                                            }}
                                        >
                                            <i className={`mdi ${isUnread ? 'mdi-email-open-outline' : 'mdi-email-outline'}`} />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        )

                    })}
                    {
                        totalMessages === 0 ?
                            <li className="text-center text-muted py-2" >
                                {searchQuery
                                    ? `No messages found for "${searchQuery}" in ${resolvedFolderDisplayName}.`
                                    : `You don't have any ${resolvedFolderDisplayName} messages.`
                                }

                            </li> : ''
                    }


                </ul>}
            </div>
            {/* card */}
            {
                !readmail && totalMessages !== 0 && (
                    <div className="row p-2">
                        <div className="col-7">
                            Showing {start} - {end} of {totalMessages}
                        </div>
                        <div className="col-5">
                            <div className="btn-group float-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    onClick={handlePrev}
                                    disabled={page === 1}
                                >
                                    <i className="fa fa-chevron-left" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    onClick={handleNext}
                                    disabled={page === totalPages}
                                >
                                    <i className="fa fa-chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >

    )
}

export default RightSidebar
